export class AsyncTrackQueue<TKey, TValue> {
  private readonly generations = new Map<TKey, number>();
  private readonly pending = new Map<
    TKey,
    {
      generation: number;
      promise: Promise<TValue>;
    }
  >();

  public run(
    key: TKey,
    factory: (generation: number) => Promise<TValue>
  ): Promise<TValue> {
    // Reuse the in-flight request for this key/generation.
    const pendingRequest = this.getPending(key);
    if (pendingRequest) {
      return pendingRequest;
    }

    const generation = this.getGeneration(key);
    // Wrap the factory so sync throws also become promise rejections.
    const promise = Promise.resolve().then(() => factory(generation));

    this.pending.set(key, {
      generation,
      promise,
    });

    const clearIfCurrent = () => {
      const current = this.pending.get(key);
      if (current?.promise === promise) {
        this.pending.delete(key);
      }
    };

    // Clear the slot when this request settles, unless a newer one replaced it.
    promise.then(clearIfCurrent, clearIfCurrent);

    return promise;
  }

  public bumpGeneration(key: TKey): number {
    const nextGeneration = this.getGeneration(key) + 1;
    this.generations.set(key, nextGeneration);
    return nextGeneration;
  }

  public getGeneration(key: TKey): number {
    return this.generations.get(key) ?? 0;
  }

  public isCurrentGeneration(key: TKey, generation: number): boolean {
    return this.getGeneration(key) === generation;
  }

  private getPending(key: TKey): Promise<TValue> | undefined {
    const pendingRequest = this.pending.get(key);
    if (!pendingRequest) {
      return undefined;
    }

    if (pendingRequest.generation !== this.getGeneration(key)) {
      return undefined;
    }

    return pendingRequest.promise;
  }
}
